import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCsvBatch,
  ingestConnectionsUntilDone,
  listConnections,
  listConnectorCatalog,
  listCsvBatches,
  removeConnection,
  saveConnection,
  syncConnectionUntilDone,
  uploadCsvSignals,
  type ConnectorTemplate,
  type CsvBatchSummary,
  type SourceConnectionPublic,
} from "@/api/connections";
import { ApiError } from "@/api/client";
import type { SourceSystem } from "@ceo-cmd/shared";

const LABELS: Record<string, string> = {
  aipoio: "aipoio",
  apollo: "Apollo",
  jira: "Jira",
  email: "Email (structured push)",
  slack: "Slack (channel pull)",
  external_dynamodb: "External DynamoDB",
};

const PULL_SYSTEMS = new Set([
  "aipoio",
  "apollo",
  "jira",
  "slack",
  "external_dynamodb",
]);

/**
 * Systems with hand-built field UI below. Anything else renders its fields
 * from the base-table connector template, so adding a connector to the
 * catalogue is enough to make it configurable here.
 */
const BESPOKE_UI = new Set(["apollo", "slack", "external_dynamodb", "email"]);

function slotKey(conn: SourceConnectionPublic): string {
  return `${conn.system}:${conn.connectionId}`;
}

export function SettingsConnectionsPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["connections"],
    queryFn: listConnections,
    staleTime: 15_000,
  });

  const { data: catalog = [] } = useQuery({
    queryKey: ["connector-catalog"],
    queryFn: listConnectorCatalog,
    staleTime: 60_000,
  });

  const [drafts, setDrafts] = useState<
    Record<
      string,
      {
        token: string;
        displayName: string;
        channelIds: string;
        tableName: string;
        region: string;
        roleArn: string;
        externalId: string;
        fieldMapJson: string;
        newEmailSlot: string;
        /** Values for catalogue-driven fields, keyed by TemplateField.key. */
        templateValues: Record<string, string>;
      }
    >
  >({});
  const [message, setMessage] = useState<string | null>(null);
  const [mintedBearer, setMintedBearer] = useState<string | null>(null);
  const [lastCsvBatch, setLastCsvBatch] = useState<{
    batchId: string;
    rowCounts: CsvBatchSummary["rowCounts"];
    exceptionsUrl?: string;
    status: string;
  } | null>(null);

  const { data: csvBatches = [] } = useQuery({
    queryKey: ["csv-batches"],
    queryFn: () => listCsvBatches(5),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (lastCsvBatch) return;
    const rejected = csvBatches.find((b) => b.rowCounts.rejected > 0);
    if (!rejected) return;
    let cancelled = false;
    void getCsvBatch(rejected.batchId)
      .then((detail) => {
        if (cancelled) return;
        setLastCsvBatch({
          batchId: rejected.batchId,
          rowCounts: rejected.rowCounts,
          exceptionsUrl: detail.downloads?.exceptionsUrl,
          status: rejected.status,
        });
      })
      .catch(() => {
        /* ignore download hydrate failures */
      });
    return () => {
      cancelled = true;
    };
  }, [csvBatches, lastCsvBatch]);

  function draft(key: string) {
    return (
      drafts[key] ?? {
        token: "",
        displayName: "",
        channelIds: "",
        tableName: "",
        region: "us-east-1",
        roleArn: "",
        externalId: "",
        fieldMapJson: '{"id":"id","title":"title","company":"companyName","summary":"summary","occurredAt":"occurredAt"}',
        newEmailSlot: "",
        templateValues: {},
      }
    );
  }

  function setDraft(
    key: string,
    patch: Partial<ReturnType<typeof draft>>,
  ) {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...draft(key), ...patch },
    }));
  }

  const save = useMutation({
    mutationFn: ({
      system,
      connectionId,
      body,
    }: {
      system: SourceSystem;
      connectionId: string;
      body: Parameters<typeof saveConnection>[2];
    }) => saveConnection(system, connectionId, body),
    onSuccess: (conn) => {
      if (conn.accessTokenOnce) {
        setMintedBearer(conn.accessTokenOnce);
        setMessage(
          `Email slot “${conn.connectionId}” saved. Copy the push bearer now — it won’t be shown again. Use header x-email-ingest-token with your session JWT on Authorization.`,
        );
      } else if (conn.status === "disconnected" || conn.lastError) {
        setMessage(
          `Saved, but not verified: ${conn.lastError ?? "check credentials and try again"}.`,
        );
      } else {
        setMessage("Connection saved.");
      }
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["today"] });
    },
    onError: (err) => {
      setMessage(err instanceof Error ? err.message : "Save failed");
    },
  });

  const remove = useMutation({
    mutationFn: ({
      system,
      connectionId,
    }: {
      system: SourceSystem;
      connectionId: string;
    }) => removeConnection(system, connectionId),
    onSuccess: () => {
      setMessage("Connection removed.");
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["today"] });
    },
  });

  const ingest = useMutation({
    mutationFn: () =>
      ingestConnectionsUntilDone((p) => {
        setMessage(
          p.done
            ? `Finishing synthesis… (${p.snapshots} raw so far)`
            : `Syncing pull feeds… chunk ${p.iteration} · ${p.snapshots} raw signals`,
        );
      }),
    onSuccess: (result) => {
      const failed = result.feeds.filter((f) => f.error).length;
      if (!result.sources.length && !failed) {
        setMessage(
          "Synced 0 items — configure Apollo, Slack, or External DynamoDB first, then Sync.",
        );
      } else {
        setMessage(
          `Synced ${result.snapshots} raw signal(s) from ${result.sources.join(", ") || "no sources"} · synthesized ${result.synthesized} Act Now move(s)${failed ? ` (${failed} feed error(s))` : ""}${result.done ? "" : " · incomplete (hit loop cap)"}.`,
        );
      }
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["ingest-batches"] });
    },
    onError: (err) => {
      setMessage(err instanceof Error ? err.message : "Ingest failed");
      void qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  const syncOne = useMutation({
    mutationFn: ({
      system,
      connectionId,
    }: {
      system: SourceSystem;
      connectionId: string;
    }) =>
      syncConnectionUntilDone(system, connectionId, (p) => {
        setMessage(
          p.done
            ? `Finishing synthesis… (${p.signals} raw / ${p.lakeWritten} lake)`
            : `Syncing ${system}… chunk ${p.iteration} · ${p.signals} raw · ${p.lakeWritten} lake`,
        );
      }),
    onSuccess: (result) => {
      const feed = result.last;
      setMessage(
        feed.error
          ? `Sync failed: ${feed.error}`
          : `Synced ${result.signals} raw signal(s) · synthesized ${result.synthesized} Act Now move(s) from ${feed.system}:${feed.connectionId}.`,
      );
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["ingest-batches"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { error?: string };
        setMessage(body.error ?? err.message);
      } else {
        setMessage(err instanceof Error ? err.message : "Sync failed");
      }
      void qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  const csvUpload = useMutation({
    mutationFn: ({ csv, filename }: { csv: string; filename?: string }) =>
      uploadCsvSignals(csv, filename),
    onSuccess: (result) => {
      setLastCsvBatch({
        batchId: result.batchId,
        rowCounts: result.rowCounts,
        exceptionsUrl: result.downloads?.exceptionsUrl,
        status: result.status,
      });
      setMessage(
        `CSV batch ${result.batchId}: ${result.rowCounts.accepted} accepted · ${result.rowCounts.rejected} rejected · ${result.rowCounts.upserted} Act Now move(s)${result.s3Skipped ? " · S3 skipped (local)" : ""}.`,
      );
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["csv-batches"] });
      void qc.invalidateQueries({ queryKey: ["ingest-batches"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as {
          batchId?: string;
          rowCounts?: CsvBatchSummary["rowCounts"];
          downloads?: { exceptionsUrl?: string };
          status?: string;
          error?: string;
        };
        if (body.batchId && body.rowCounts) {
          setLastCsvBatch({
            batchId: body.batchId,
            rowCounts: body.rowCounts,
            exceptionsUrl: body.downloads?.exceptionsUrl,
            status: body.status ?? "failed",
          });
          setMessage(
            `CSV batch ${body.batchId} failed: ${body.error ?? err.message}`,
          );
          void qc.invalidateQueries({ queryKey: ["csv-batches"] });
      void qc.invalidateQueries({ queryKey: ["ingest-batches"] });
          return;
        }
      }
      setMessage(err instanceof Error ? err.message : "CSV upload failed");
    },
  });

  const emailSlots = connections.filter((c) => c.system === "email");
  const otherConns = connections.filter((c) => c.system !== "email");

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Connections</h1>
        <p className="text-muted-foreground">
          Feeds enter NORMALIZE as Command Signals. Pull: Apollo, Slack
          channels, external DynamoDB. Push: structured email events and CSV.
          HubSpot is deferred.{" "}
          <Link to="/docs/feeds" className="text-brand underline">
            Feeds guide
          </Link>
          {" · "}
          <Link to="/docs" className="text-brand underline">
            Help & docs
          </Link>
        </p>
      </header>

      {message && (
        <p className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground">
          {message}
        </p>
      )}

      {mintedBearer && (
        <div className="rounded-md border border-amber-500/40 bg-card px-3 py-2 text-sm">
          <p className="font-medium text-foreground">Email push bearer</p>
          <code className="mt-1 block break-all text-xs text-foreground">
            {mintedBearer}
          </code>
          <p className="mt-1 text-xs text-muted-foreground">
            POST /api/normalize/email/:connectionId with session JWT on
            Authorization and the push bearer on x-email-ingest-token.
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {!isLoading && (
        <div className="grid gap-4">
          {otherConns.map((conn) => {
            const key = slotKey(conn);
            const d = draft(key);
            return (
              <ConnectionCard
                key={key}
                conn={conn}
                draft={d}
                template={catalog.find((t) => t.system === conn.system)}
                onDraft={(patch) => setDraft(key, patch)}
                busy={
                  save.isPending || remove.isPending || syncOne.isPending
                }
                onSave={() => {
                  if (!BESPOKE_UI.has(conn.system)) {
                    const template = catalog.find(
                      (t) => t.system === conn.system,
                    );
                    const fields = template?.fields ?? [];
                    const missing = fields
                      .filter(
                        (f) =>
                          f.required &&
                          !d.templateValues[f.key]?.trim() &&
                          !conn.configured,
                      )
                      .map((f) => f.label);
                    if (missing.length > 0) {
                      setMessage(`Required: ${missing.join(", ")}.`);
                      return;
                    }
                    // Secrets go in config alongside the rest — the backend
                    // reads each connector's credential from where its
                    // template declares it.
                    const config: Record<string, unknown> = {};
                    for (const f of fields) {
                      const value = d.templateValues[f.key]?.trim();
                      if (!value) continue;
                      config[f.key] =
                        f.type === "list"
                          ? value.split(/[,\s]+/).filter(Boolean)
                          : value;
                    }
                    save.mutate({
                      system: conn.system,
                      connectionId: conn.connectionId,
                      body: {
                        displayName: d.displayName || undefined,
                        config,
                      },
                    });
                    return;
                  }
                  if (conn.system === "apollo") {
                    if (!d.token.trim() && !conn.configured) {
                      setMessage("Enter an Apollo API key first.");
                      return;
                    }
                    save.mutate({
                      system: "apollo",
                      connectionId: conn.connectionId,
                      body: {
                        accessToken: d.token.trim() || undefined,
                        displayName: d.displayName || undefined,
                      },
                    });
                    return;
                  }
                  if (conn.system === "slack") {
                    if (!d.token.trim() && !conn.configured) {
                      setMessage("Enter a Slack bot token (xoxb-…) first.");
                      return;
                    }
                    const channelIds = d.channelIds
                      .split(/[,\s]+/)
                      .map((s) => s.trim())
                      .filter(Boolean);
                    if (channelIds.length === 0) {
                      setMessage("Enter at least one Slack channel ID.");
                      return;
                    }
                    save.mutate({
                      system: "slack",
                      connectionId: conn.connectionId,
                      body: {
                        accessToken: d.token.trim() || undefined,
                        displayName: d.displayName || undefined,
                        config: { channelIds },
                      },
                    });
                    return;
                  }
                  if (conn.system === "external_dynamodb") {
                    if (!d.tableName.trim() || !d.roleArn.trim()) {
                      setMessage("Table name and role ARN are required.");
                      return;
                    }
                    let fieldMap: Record<string, string> = {};
                    try {
                      fieldMap = JSON.parse(d.fieldMapJson) as Record<
                        string,
                        string
                      >;
                    } catch {
                      setMessage("Field map must be valid JSON.");
                      return;
                    }
                    save.mutate({
                      system: "external_dynamodb",
                      connectionId: conn.connectionId,
                      body: {
                        displayName: d.displayName || undefined,
                        config: {
                          tableName: d.tableName.trim(),
                          region: d.region.trim() || "us-east-1",
                          roleArn: d.roleArn.trim(),
                          externalId: d.externalId.trim() || undefined,
                          fieldMap,
                        },
                      },
                    });
                  }
                }}
                onRemove={() =>
                  remove.mutate({
                    system: conn.system,
                    connectionId: conn.connectionId,
                  })
                }
                onSync={
                  PULL_SYSTEMS.has(conn.system) && conn.configured
                    ? () =>
                        syncOne.mutate({
                          system: conn.system,
                          connectionId: conn.connectionId,
                        })
                    : undefined
                }
              />
            );
          })}

          <section className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">
              Email — structured push (not live mailbox)
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Up to 3 named slots. Providers POST parsed events; this is not
              Gmail/IMAP.
            </p>
            <div className="mt-3 grid gap-3">
              {emailSlots.map((conn) => {
                const key = slotKey(conn);
                const d = draft(key);
                return (
                  <div
                    key={key}
                    className="rounded-md border border-border p-3"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {conn.displayName || conn.connectionId}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Status: {conn.status}
                          {conn.configured ? " · configured" : ""}
                          {conn.lastSuccessAt
                            ? ` · last ${new Date(conn.lastSuccessAt).toLocaleString()}`
                            : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({
                              system: "email",
                              connectionId: conn.connectionId,
                              body: {
                                displayName: d.displayName || undefined,
                                rotateBearer: true,
                              },
                            })
                          }
                          className="text-xs font-medium text-brand hover:underline"
                        >
                          {conn.configured ? "Rotate bearer" : "Create bearer"}
                        </button>
                        {conn.configured && (
                          <button
                            type="button"
                            disabled={remove.isPending}
                            onClick={() =>
                              remove.mutate({
                                system: "email",
                                connectionId: conn.connectionId,
                              })
                            }
                            className="text-xs font-medium text-muted-foreground hover:text-destructive"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {emailSlots.length < 3 && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    placeholder="New slot id (e.g. ceo-inbox)"
                    value={draft("email:new").newEmailSlot}
                    onChange={(e) =>
                      setDraft("email:new", {
                        newEmailSlot: e.target.value,
                      })
                    }
                    className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() => {
                      const id = draft("email:new").newEmailSlot.trim();
                      if (!id) {
                        setMessage("Enter a slot id.");
                        return;
                      }
                      if (id.includes("@")) {
                        setMessage(
                          "Use a short slug like ceo-inbox — not an email address.",
                        );
                        return;
                      }
                      save.mutate({
                        system: "email",
                        connectionId: id,
                        body: { displayName: id, rotateBearer: true },
                      });
                      setDraft("email:new", { newEmailSlot: "" });
                    }}
                    className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-hover"
                  >
                    Add email slot
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">Upload CSV</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Required columns: id, company_name, title, situation_summary,
          recommendation, why_now, expected_outcome, risk_of_inaction,
          occurred_at. Files are warehoused in S3 (original / processed /
          exceptions). View all ingest batches in{" "}
          <Link to="/settings/observability" className="text-brand underline">
            Observability
          </Link>
          .
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="text-sm text-foreground"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                csvUpload.mutate({
                  csv: String(reader.result ?? ""),
                  filename: file.name,
                });
              };
              reader.readAsText(file);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={csvUpload.isPending}
            onClick={() => fileRef.current?.click()}
            className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-hover disabled:opacity-50"
          >
            {csvUpload.isPending ? "Uploading…" : "Choose CSV file"}
          </button>
        </div>
        {lastCsvBatch && (
          <div className="mt-3 rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground">
            <p>
              Last batch{" "}
              <span className="font-mono">{lastCsvBatch.batchId}</span> ·{" "}
              {lastCsvBatch.status} · accepted{" "}
              {lastCsvBatch.rowCounts.accepted} · rejected{" "}
              {lastCsvBatch.rowCounts.rejected} · upserted{" "}
              {lastCsvBatch.rowCounts.upserted}
            </p>
            {lastCsvBatch.exceptionsUrl &&
              lastCsvBatch.rowCounts.rejected > 0 && (
                <a
                  href={lastCsvBatch.exceptionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block font-medium text-brand hover:underline"
                >
                  Download exceptions
                </a>
              )}
          </div>
        )}
        {csvBatches.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {csvBatches.slice(0, 3).map((b) => (
              <li key={b.batchId} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-foreground">{b.batchId}</span>
                <span>
                  {b.rowCounts.accepted} accepted / {b.rowCounts.rejected}{" "}
                  rejected
                </span>
                {b.rowCounts.rejected > 0 &&
                  lastCsvBatch?.batchId === b.batchId &&
                  lastCsvBatch.exceptionsUrl && (
                    <a
                      href={lastCsvBatch.exceptionsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-brand hover:underline"
                    >
                      Download exceptions
                    </a>
                  )}
                {b.rowCounts.rejected > 0 &&
                  lastCsvBatch?.batchId !== b.batchId && (
                    <button
                      type="button"
                      className="font-medium text-brand hover:underline"
                      onClick={() => {
                        void getCsvBatch(b.batchId)
                          .then((detail) => {
                            setLastCsvBatch({
                              batchId: b.batchId,
                              rowCounts: b.rowCounts,
                              exceptionsUrl: detail.downloads?.exceptionsUrl,
                              status: b.status,
                            });
                          })
                          .catch(() => {
                            setMessage("Could not load exceptions download.");
                          });
                      }}
                    >
                      Download exceptions
                    </button>
                  )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <button
          type="button"
          disabled={ingest.isPending}
          onClick={() => ingest.mutate()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
        >
          {ingest.isPending ? "Syncing…" : "Sync all pull feeds"}
        </button>
        <Link
          to="/"
          className="text-sm font-medium text-brand hover:underline"
        >
          Back to Today
        </Link>
      </div>
    </div>
  );
}

function ConnectionCard({
  conn,
  draft,
  template,
  onDraft,
  onSave,
  onRemove,
  onSync,
  busy,
}: {
  conn: SourceConnectionPublic;
  draft: {
    token: string;
    displayName: string;
    channelIds: string;
    tableName: string;
    region: string;
    roleArn: string;
    externalId: string;
    fieldMapJson: string;
    templateValues: Record<string, string>;
  };
  template?: ConnectorTemplate;
  onDraft: (patch: Partial<typeof draft>) => void;
  onSave: () => void;
  onRemove: () => void;
  onSync?: () => void;
  busy?: boolean;
}) {
  const label = LABELS[conn.system] ?? template?.name ?? conn.system;
  const usesTemplateFields = !BESPOKE_UI.has(conn.system);
  const existingChannels = Array.isArray(conn.config?.channelIds)
    ? (conn.config!.channelIds as string[]).join(", ")
    : "";

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">
            Slot: {conn.connectionId}
            {" · "}
            Status: {conn.status}
            {conn.configured ? " · configured" : " · not configured"}
            {conn.lastSuccessAt
              ? ` · last success ${new Date(conn.lastSuccessAt).toLocaleString()}`
              : ""}
          </p>
          {conn.system === "apollo" && (
            <p className="mt-1 text-xs text-muted-foreground">
              Sync pulls open tasks into the signal lake in pages (avoids
              timeouts); synthesis builds the Act Now queue when the walk
              finishes.
            </p>
          )}
          {usesTemplateFields && template?.description && (
            <p className="mt-1 text-xs text-muted-foreground">
              {template.description}
            </p>
          )}
          {conn.lastError && (
            <p className="mt-1 text-xs text-amber-600">{conn.lastError}</p>
          )}
        </div>
        <div className="flex gap-2">
          {onSync && (
            <button
              type="button"
              disabled={busy}
              onClick={onSync}
              className="text-xs font-medium text-brand hover:underline"
            >
              Sync now
            </button>
          )}
          {conn.configured && (
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              className="text-xs font-medium text-muted-foreground hover:text-destructive"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 grid gap-2">
        {usesTemplateFields &&
          (template?.fields ?? []).map((field) => {
            const stored = conn.config?.[field.key];
            const storedHint =
              field.type !== "secret" && typeof stored === "string" && stored
                ? ` (current: ${stored})`
                : "";
            return (
              <label key={field.key} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">
                  {field.label}
                  {field.required ? " *" : ""}
                </span>
                {field.type === "json" ? (
                  <textarea
                    rows={3}
                    placeholder={field.placeholder}
                    value={draft.templateValues[field.key] ?? ""}
                    onChange={(e) =>
                      onDraft({
                        templateValues: {
                          ...draft.templateValues,
                          [field.key]: e.target.value,
                        },
                      })
                    }
                    className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
                  />
                ) : (
                  <input
                    type={field.type === "secret" ? "password" : "text"}
                    {...(field.type === "secret"
                      ? {
                          autoComplete: "new-password",
                          name: `${conn.system}-${field.key}`,
                          "data-1p-ignore": true,
                          "data-lpignore": "true",
                        }
                      : {})}
                    placeholder={
                      field.placeholder ??
                      field.defaultValue ??
                      `${field.label}${storedHint}`
                    }
                    value={draft.templateValues[field.key] ?? ""}
                    onChange={(e) =>
                      onDraft({
                        templateValues: {
                          ...draft.templateValues,
                          [field.key]: e.target.value,
                        },
                      })
                    }
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                )}
                {field.help && (
                  <span className="text-xs text-muted-foreground">
                    {field.help}
                  </span>
                )}
              </label>
            );
          })}

        {(conn.system === "apollo" || conn.system === "slack") && (
          <input
            type="password"
            name={`${conn.system}-api-token`}
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            placeholder={
              conn.system === "apollo"
                ? "Apollo API key"
                : "Slack bot token (xoxb-…)"
            }
            value={draft.token}
            onChange={(e) => onDraft({ token: e.target.value })}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        )}

        {conn.system === "slack" && (
          <input
            type="text"
            placeholder={
              existingChannels
                ? `Channels (current: ${existingChannels})`
                : "Channel IDs (comma-separated, max 3)"
            }
            value={draft.channelIds}
            onChange={(e) => onDraft({ channelIds: e.target.value })}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        )}

        {conn.system === "external_dynamodb" && (
          <>
            <input
              type="text"
              placeholder="Table name"
              value={draft.tableName}
              onChange={(e) => onDraft({ tableName: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <input
              type="text"
              placeholder="Region"
              value={draft.region}
              onChange={(e) => onDraft({ region: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <input
              type="text"
              placeholder="IAM role ARN"
              value={draft.roleArn}
              onChange={(e) => onDraft({ roleArn: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <input
              type="text"
              placeholder="External ID (optional)"
              value={draft.externalId}
              onChange={(e) => onDraft({ externalId: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <textarea
              rows={3}
              placeholder="Field map JSON"
              value={draft.fieldMapJson}
              onChange={(e) => onDraft({ fieldMapJson: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
            />
          </>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onSave}
            className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-hover disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
